/**
 * `/tajweed` — the 17 rules, in full, as a reference page (§6, §8.1).
 *
 * Uses TAJWEED_RULE_ORDER rather than alphabetical: that order is also a sensible
 * teaching order (§8.2).
 */
import { createFileRoute } from '@tanstack/react-router'
import { ORTHOGRAPHIC_RULES, TAJWEED_RULE_ORDER } from '@mushaf/corpus'
import { SecondaryShell } from '../components/SecondaryShell'
import { RuleCard } from '../components/panel/RuleCard'
import { useMeta } from '../lib/queries'

export const Route = createFileRoute('/tajweed')({ component: TajweedPage })

function TajweedPage() {
  const { data: meta } = useMeta()

  // L27 — every Arabic node carries its own `lang`/`dir` under this fork's Latin root.
  // This route's body copy is still Arabic BY PLAN (Phase 7 replaces it with a join
  // against MIT-licensed data rather than a guessed gloss — `madda_obligatory` merges
  // what the standard reference splits in two, so an authored translation would teach a
  // wrong distinction confidently). Until then the law still applies: without these
  // attributes the prose gets a Latin fallback face and an English screen-reader voice.
  return (
    <SecondaryShell
      title={
        <span lang="ar" dir="rtl">
          أحكام التجويد
        </span>
      }
    >
      <p lang="ar" dir="rtl" className="text-xs leading-6 text-[var(--ink-faint)]">
        سبعة عشر حكمًا، مرتّبة كما تُدرَّس: المدود أوّلًا لأنّها تغيّر مقدار ما تمدّه، ثمّ الغنن
        والإخفاء والإدغام، وآخرها ما هو رسمٌ لا نطق. كل مثال هنا مأخوذ من المصحف نفسه.
      </p>

      <div className="grid gap-3 md:grid-cols-2">
        {TAJWEED_RULE_ORDER.map((id) => (
          <div key={id} className="grid gap-1">
            {ORTHOGRAPHIC_RULES.has(id) && (
              <span lang="ar" dir="rtl" className="text-[11px] text-[var(--ink-faint)]">
                حكم رسمي — لا يغيّر المدّ
              </span>
            )}
            <RuleCard id={id} meta={meta} />
          </div>
        ))}
      </div>

      {/* L18, stated to the reader rather than hidden. */}
      <p
        lang="ar"
        dir="rtl"
        className="rounded-xl bg-[var(--surface-2)] p-3 text-[11px] leading-6 text-[var(--ink-faint)]"
      >
        ألوان «خطّ الملك فهد الملوّن» مُثبتة داخل الخطّ نفسه ولا يمكن تغييرها، فبعضها أقلّ وضوحًا
        على بعض ألوان الورق. أمّا «تلوين الكلمات» فهو تقريبي: الحرف الواحد في خطّ المصحف جزءٌ من رسم
        الكلمة كلّها، فلا يمكن تلوين بعضها دون بعض، ولذلك يُختار للكلمة حكمٌ واحد بحسب الترتيب
        أعلاه.
      </p>
    </SecondaryShell>
  )
}
